import { useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Checkbox,
  Dropdown,
  Icon,
  Image,
  SemanticCOLORS,
} from 'semantic-ui-react';

import Dragonfly_icon_white from '../../../assets/Dragonfly_icon_white.svg';
import config from '../../../config';
import useElementVisibility from '../../../hooks/useElementVisibility';
import { ROLES } from '../../../libs/Constants';
import RoleCheck from '../../../libs/RoleCheck';
import {
  ADMIN_LABEL,
  ADVANCED_DRAGONFLY_REPORTING_LABEL,
  ADVISE_REPORTING_LABEL,
  AUTO_ASSIGN_LABEL,
  CMS_IPO_LIST_LABEL,
  DASHBOARD_LABEL,
  QUEUE_LABEL,
  REPORTING_LABEL,
  SEARCH_LABEL,
  TOOLS_LABEL,
} from '../MainHeader.constants';
import { TeamStats, UserRole } from '../MainHeader.types';
import TeamPendingStats from './TeamPendingStats';
import UserMenu from './UserMenu';

interface NavigationMenuBarProps {
  userRoles: UserRole[];
  teamStats: TeamStats[];
  totalPending: number;
  colors: SemanticCOLORS[];
  handleAutoAssignClick: () => void;
  iamworking: boolean;
  username: string;
  handleProfileClick: () => void;
  handleLogoutClick: () => void;
}

const NavigationMenuBar = ({
  userRoles,
  teamStats,
  totalPending,
  colors,
  handleAutoAssignClick,
  iamworking,
  username,
  handleProfileClick,
  handleLogoutClick,
}: NavigationMenuBarProps) => {
  const hideUserMenu = useElementVisibility('main-logo-header');

  const handleQueueClick = useCallback(() => {
    sessionStorage.removeItem('selectedPopulation');
  }, []);

  const hasReportingAccess = useMemo(() => {
    return RoleCheck(userRoles, [
      ROLES.SYSTEM_ADMINISTRATOR,
      ROLES.ADMINISTRATOR,
      ROLES.TEAM_LEAD,
      ROLES.SUPPORT,
    ]);
  }, [userRoles]);

  const hasAdminAccess = useMemo(() => {
    return RoleCheck(userRoles, [
      ROLES.SYSTEM_ADMINISTRATOR,
      ROLES.ADMINISTRATOR,
    ]);
  }, [userRoles]);

  const hasPhysicianAdvisorAccess = useMemo(() => {
    return RoleCheck(userRoles, [ROLES.PHYSICIAN_ADVISOR]);
  }, [userRoles]);

  return (
    <div className='app-navigation-bar'>
      {!hideUserMenu && (
        <NavLink to='/'>
          <Image src={Dragonfly_icon_white} />
        </NavLink>
      )}
      <TeamPendingStats
        teamStats={teamStats}
        totalPending={totalPending}
        colors={colors}
      />
      <NavLink to='/dashboard'>
        <Icon name='dashboard' />
        {DASHBOARD_LABEL}
      </NavLink>
      <NavLink to='/queue' onClick={handleQueueClick}>
        <Icon name='tasks' />
        {QUEUE_LABEL}
      </NavLink>
      {hasReportingAccess && (
        <Dropdown
          className='navigation-menu-item'
          icon={<Icon name='area chart' />}
          trigger={REPORTING_LABEL}
        >
          <Dropdown.Menu>
            <Dropdown.Item as={NavLink} to='/reporting'>
              {ADVISE_REPORTING_LABEL}
            </Dropdown.Item>
            <Dropdown.Item as='a' target='_blank' href={config.REPORTING_URL}>
              {ADVANCED_DRAGONFLY_REPORTING_LABEL}
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}
      {hasAdminAccess && (
        <NavLink to='/admin'>
          <Icon name='users' />
          {ADMIN_LABEL}
        </NavLink>
      )}
      <NavLink to='/search'>
        <Icon name='search' />
        {SEARCH_LABEL}
      </NavLink>
      <Dropdown
        className='navigation-menu-item'
        icon={<Icon name='wrench' />}
        trigger={TOOLS_LABEL}
      >
        <Dropdown.Menu>
          <Dropdown.Item data-cms-link='data-cms-link' as={NavLink} to='/cms'>
            {CMS_IPO_LIST_LABEL}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      {hasPhysicianAdvisorAccess && (
        <Checkbox
          className='ui white'
          data-testid='auto-assign-checkbox'
          label={AUTO_ASSIGN_LABEL}
          onClick={handleAutoAssignClick}
          checked={iamworking}
        />
      )}
      {!hideUserMenu && (
        <UserMenu
          username={username}
          handleProfileClick={handleProfileClick}
          handleLogoutClick={handleLogoutClick}
        />
      )}
    </div>
  );
};

export default NavigationMenuBar;
